import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useBreakpoints } from '~/hooks/useMediaQuery';
// eslint-disable-next-line import/no-extraneous-dependencies
import { AppstoreOutlined, MenuOutlined, PaperClipOutlined, UserOutlined } from '@ant-design/icons';
import jwtDecode from 'jwt-decode';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import logo from '~/assets/images/logo.png';
import { PermissionNames } from '~/constants/permissionNames';
import { BIDashboardContext } from '~/context/BIDashboardContext';
import * as API from '~/domain/api';
import { MenuClickSource } from '~/domain/enum/menuClickSource.enum';
import * as M from '~/domain/model';
import { registerAccess } from '~/services/quickAccess';
import { useAccessTokenFromStore } from '~/hooks/useAccessTokenFromStore';
import { actions, selectors } from '~/store/modules';
import { useAuthStore } from '~/store/modules/auth/useAuthStore';
import { fetchBiDashboard } from '~/store/modules/bidashboards/apiCalls';
import { getBiDashboardPagination, getBiDashboards } from '~/store/modules/bidashboards/selectors';
import { addTab } from '~/store/modules/tabs/actions';
import DefaultBiDashboard from '../pages/DefaultBiDashboard';
import OtherPlatforms from '../pages/OtherPlatforms';
import ServiceRequests from '../pages/ServiceRequests';
import { menus, MenuType } from './menus';
import * as S from './styles';

const Menu: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const posthog = usePostHog();

  const permissions = useSelector(selectors.ui.auth.getPermissions);
  const profile = useSelector(selectors.ui.auth.getProfile);

  const { formFilter } = useContext(BIDashboardContext);
  const biDashboards = useSelector(getBiDashboards);
  const biDashboardPagination = useSelector(getBiDashboardPagination);

  const { isNotDesktop: isMobile } = useBreakpoints();
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);

  function onCollapse(collapsedItem: boolean) {
    setCollapsed(collapsedItem);
  }

  const openMobile = useCallback(() => setMobileOpen(true), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  function handleMenuClick(menu: MenuType) {
    registerAccess(profile.veiling_claim_type_identifier, menu.key);
    posthog.capture('menu_click', {
      menu: menu.key,
      menuName: menu.name,
      permissionName: menu.permissionName,
      userName: profile.veiling_claim_type_name,
      source: MenuClickSource.Menu,
    });
  }

  useEffect(() => {
    const biDashboardParams: M.FindBiDashboardParams = {
      name: formFilter?.getFieldValue('name'),
      status: formFilter?.getFieldValue('status'),
      ...biDashboardPagination,
    };
    dispatch(fetchBiDashboard(biDashboardParams));
  }, []);

  const accessToken = useAccessTokenFromStore();

  useEffect(() => {

    dispatch(actions.ui.auth.fetchPermissions());
    const accessData: API.Profile = jwtDecode(accessToken);
    dispatch(
      actions.ui.auth.setProfile({
        veiling_claim_type_email: accessData.veiling_claim_type_email,
        veiling_claim_type_identifier: accessData.veiling_claim_type_identifier,
        veiling_claim_type_name: accessData.veiling_claim_type_name,
        veiling_claim_type_role: accessData.veiling_claim_type_role,
      }),
    );
  }, []);

  const otherPlatforms: MenuType = {
    key: 'otherPlatforms',
    name: 'menu.otherPlatforms',
    title: 'menu.otherPlatforms',
    component: () => <OtherPlatforms />,
  };

  const serviceRequests: MenuType = {
    key: 'serviceRequests',
    name: 'Abertura de Chamado',
    title: 'Abertura de Chamado',
    component: () => <ServiceRequests />,
  };

  const renderSubMenus = useCallback(
    (menuItems: MenuType[]): React.ReactNode[] => {
      const items = menuItems.map(item => {
        const hasPermission =
          permissions?.findIndex(p => p.permissionName === item.permissionName) !== -1;

        if (hasPermission) {
          if (item.MenuItems && item.MenuItems.length > 0) {
            return (
              <S.SubMenu key={item.key} title={t(item.title)}>
                {renderSubMenus(item.MenuItems)}
              </S.SubMenu>
            );
          }
          return (
            <S.MenuItem
              key={item.key}
              onClick={() => {
                dispatch(addTab(item));
                closeMobile();
                handleMenuClick(item);
              }}
            >
              {t(item.title)}
            </S.MenuItem>
          );
        }
        if (item.permissionName === 'MOCK') {
          return (
            <S.MenuItem
              key={item.key}
              onClick={() => {
                dispatch(addTab(item));
                closeMobile();
              }}
            >
              [MOCK] {t(item.title)}
            </S.MenuItem>
          );
        }
        return null;
      });
      return items;
    },
    [dispatch, permissions, t, closeMobile],
  );

  const renderMenu = useMemo(() => {
    const mainMenu: any = menus;
    const index = mainMenu.findIndex((i: any) => i.key === 'indicatorsPanel');
    if (index !== -1 && mainMenu[index]) {
      const permissionIds = permissions?.map((p: any) => p.id) || [];
      const allowedDashboards = biDashboards.filter(
        (dashboard: any) =>
          !dashboard.permissionId || permissionIds.includes(dashboard.permissionId),
      );

      if (allowedDashboards.length) {
        const groupedDashboards: Record<string, MenuType[]> = {};

        allowedDashboards.forEach((dashboard: any) => {
          const originalIndex = biDashboards.findIndex((d: any) => d.id === dashboard.id);
          const groupName = dashboard.groupDescription || 'Outros';

          const dashboardPermission = permissions?.find(
            (p: any) => p.id === dashboard.permissionId
          );
          const menuItem: MenuType = {
            key: `dashboard-${dashboard.id}`,
            name: dashboard.name,
            component: () => <DefaultBiDashboard index={originalIndex} />,
            title: dashboard.name,
            permissionName: dashboardPermission?.permissionName ?? PermissionNames.MenuIndicatorsPanel,
          };

          if (!groupedDashboards[groupName]) {
            groupedDashboards[groupName] = [];
          }
          groupedDashboards[groupName].push(menuItem);
        });

        const sortedGroups = Object.keys(groupedDashboards).sort((a, b) =>
          a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }),
        );

        mainMenu[index].MenuItems = sortedGroups.map(groupName => {
          const normalizedGroup = groupName
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .split(' ')
            .join('');

          const sortedItems = groupedDashboards[groupName].sort((a: any, b: any) =>
            a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }),
          );

          return {
            key: `indicators-${normalizedGroup}`,
            name: groupName,
            title: groupName,
            permissionName: PermissionNames.MenuIndicatorsPanel,
            MenuItems: sortedItems,
          };
        });
      }
    }

    const items: any = mainMenu.map((menu: MenuType) => {
      if (permissions?.findIndex(p => p.permissionName === menu.permissionName) !== -1) {
        if (!menu.MenuItems) {
          return (
            <S.MenuItem
              key={menu.key}
              icon={menu.icon}
              onClick={() => {
                dispatch(addTab(menu));
                closeMobile();
                handleMenuClick(menu);
                dispatch(addTab(menu))
              }}>
              {t(menu.title)}
            </S.MenuItem>
          );
        }
        return (
          <S.SubMenu key={menu.key} icon={menu.icon} title={t(menu.title)}>
            {renderSubMenus(menu.MenuItems)}
          </S.SubMenu>
        );
      }
    });

    return items;
  }, [biDashboards, dispatch, permissions, renderSubMenus, t, closeMobile]);

  const logout = useCallback(() => {
    useAuthStore.logout();
  }, []);

  return (
    <>
      {isMobile && !mobileOpen && (
        <S.MobileHeader>
          <S.MobileLogo src={logo} alt="Veiling" />
          <S.HamburgerButton aria-label="Open menu" onClick={openMobile}>
            <MenuOutlined />
          </S.HamburgerButton>
        </S.MobileHeader>
      )}
      {isMobile && mobileOpen && <S.Backdrop onClick={closeMobile} />}
      <S.Sider
        collapsible={!isMobile}
        collapsed={isMobile ? false : collapsed}
        onCollapse={onCollapse}
        width={256}
        className={`${isMobile ? 'isMobile' : ''} ${isMobile && mobileOpen ? 'mobileOpen' : ''}`.trim()}
      >
        <div>
          <S.Logo className={collapsed ? 'collapsed' : ''}>
            <img loading="lazy" src={logo} alt="" />
          </S.Logo>

          <S.MenuContainer defaultSelectedKeys={['1']} mode="inline">
            <S.MenuGroup key="navigation" className={collapsed || isMobile ? 'isMobile' : ''}>
              {renderMenu}
            </S.MenuGroup>
          </S.MenuContainer>
        </div>

        <div>
          <S.ProfileContent
            className={`${collapsed ? 'collapsed' : ''} ${isMobile ? 'isMobile' : ''}`}
          >
            <div>
              <S.MenuContainer mode="inline">
                <S.MenuGroup key="navigation" className={collapsed || isMobile ? 'isMobile' : ''}>
                  <S.MenuItem
                    icon={<AppstoreOutlined />}
                    key={1}
                    onClick={() => {
                      window.location.href = String(`${process.env.REACT_APP_SINGLE_SIGNON}apps`);
                    }}
                  >
                    {t('menu.apps')}
                  </S.MenuItem>
                  <S.MenuItem
                    icon={<PaperClipOutlined />}
                    key={2}
                    onClick={() => dispatch(addTab(otherPlatforms))}
                  >
                    {t('menu.otherPlatforms')}
                  </S.MenuItem>
                  <S.SubMenu
                    key={2.1}
                    icon={<UserOutlined />}
                    title={profile.veiling_claim_type_name}
                  >
                    <S.MenuItem key={2.11} onClick={() => {
                      handleMenuClick(serviceRequests);
                      dispatch(addTab(serviceRequests))
                    }}>
                      Abertura de Chamados
                    </S.MenuItem>

                    <S.MenuItem
                      key={2.12}
                      onClick={() => {
                        logout();
                      }}
                    >
                      {t('menu.logoff')}
                    </S.MenuItem>
                  </S.SubMenu>
                </S.MenuGroup>
              </S.MenuContainer>
            </div>
          </S.ProfileContent>
        </div>
      </S.Sider>
    </>
  );
};
export default memo(Menu);
